import { CommonModule } from '@angular/common'
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    SimpleChanges,
    ViewChild,
} from '@angular/core'
import { AvailableTimesService, TimeSlotData } from '@app/core/_services/available-times.service'
import { AvailablePartnerTileComponent } from '../available-partner-tile/available-partner-tile.component'
import {
    FmWindowResizedDirective,
    WindowResizedType,
} from '@app/shared/directives/fm-window-resized.directive'
import {
    FIFTEEN_MINS_IN_MSECS,
    FIVE_MINUTES_IN_MSECS,
    LATE_BOOKING_GRACE_PERIOD_MINUTES,
    SelectedSession,
    SelectedSessionService,
    UtilsService,
} from '@app/core/_services'
import { UserService } from '@app/core/_services/user.service'
import { FeatureCheckService } from '@app/core/_services/feature-check.service'
import {
    MeetingsParticipantInfo,
    MeetingsBookingsGroupMeeting,
    MeetingsBookingsPairedMeeting,
    MeetingsParticipantsMap,
    SESSION_DURATION_25_MINS,
    TimeFormat,
} from '@app/core/_models'
import { SessionSelectorOutlineComponent } from '../session-selector-outline/session-selector-outline.component'
import { SelectedSessionTileComponent } from '../selected-session-tile/selected-session-tile.component'
import { TimeUtilitiesService } from '@app/core/_services/time-utilities.service'
import { SessionSettingsService } from '@app/core/_services/session-settings.service'
import { BookedSessionTileComponent } from '../booked-session-tile/booked-session-tile.component'
import { EpochToTimePipe } from '@app/core/pipes/epoch-to-time.pipe'
import { SkeletonModule } from 'primeng/skeleton'
import { Subject, takeUntil } from 'rxjs'
import { DstBoundaries, DstBoundaryService } from '@app/core/_services/dst-service'
import { GroupMeetingTileComponent } from '@app/routes/dashboard/components/group-meeting-tile/group-meeting-tile.component'
import { GroupMeetingCardVm } from '@app/core/_services/group-meetings-store.service'

// NOTE: Defining again here since importing this component in two
// different routes /dashboard and /sessions is causing a circular dependency
// on the calendar_day component. TODO: Move all constants to a shared file?
const NUM_TIMESLOTS_PER_DAY = 24 * 4 + 3
const TIMESLOT_HEIGHT = 48 // Each 15minutes slot is 48px high
// one selectable slot for each 15 minute increment (0 indexed)
export const MAXIMUM_SELECTABLE_TIMESLOT_NUMBER = 24 * 4 - 1

export interface CalendarDayAvailablePartner extends TimeSlotData {
    slot: number // Determine where this will appear on the calendar
    sessionTime: number
    sessionDuration: number
    isSelectable?: boolean
    availableGroupMeetings: GroupMeetingCardVm[]
    hasAvailableGroupMeeting: boolean
}

export interface CalendarDayData {
    startOfDay: number
    potentialPartnerData: Array<CalendarDayAvailablePartner>
    bookedSessionData: Array<CalendarDayBookedSession>
    bookedGroupMeetingData: Array<CalendarDayBookedGroupMeeting>
    selectedSessionData: Array<CalendarDaySelectedSession>
}

interface EmptyTimeSlotData {
    index: number
    startTime: number
    isSelectable: boolean
    isDst: boolean
}
export interface CalendarDayBookedSession extends MeetingsBookingsPairedMeeting {
    slot: number // Determine where this will appear on the calendar
    partner: MeetingsParticipantInfo | null
}

export interface CalendarDayBookedGroupMeeting extends MeetingsBookingsGroupMeeting {
    slot: number // Determine where this will appear on the calendar
    participants: MeetingsParticipantInfo[]
    //participantsMap: MeetingsParticipantsMap
}

export interface CalendarDaySelectedSession extends SelectedSession {
    slot: number // Determine where this will appear on the calendar
}
@Component({
    selector: 'fm-calendar-day',
    imports: [
        EpochToTimePipe,
        CommonModule,
        AvailablePartnerTileComponent,
        FmWindowResizedDirective,
        SessionSelectorOutlineComponent,
        SelectedSessionTileComponent,
        BookedSessionTileComponent,
        GroupMeetingTileComponent,
        SkeletonModule,
    ],
    templateUrl: './calendar-day.component.html',
    styleUrl: './calendar-day.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarDayComponent {
    @ViewChild('dayContainer', { read: ElementRef, static: true }) dayContainer: ElementRef

    @Input() isToday: boolean
    @Input() dayData: CalendarDayData
    @Input() timezone: string
    @Input() timeFormat: TimeFormat = '12hr'
    @Input() sessionDuration: number = SESSION_DURATION_25_MINS
    @Input() isFirstDay: boolean // Round out the bottom left of calendar
    @Input() isLastDay: boolean // Round out the bottom right of calendar

    private ngUnsubscribe = new Subject<any>()

    public fifteenMinsInMs = FIFTEEN_MINS_IN_MSECS
    public endOfDay: number
    public allTimeslots: EmptyTimeSlotData[] = []

    // Remove data the is covered up by confirmed or selected sessions
    // to avoid the need for z-index and better memory efficiency
    public filteredPotentialPartnerData: Array<CalendarDayAvailablePartner>

    // Determine which timeslot is being pointed to
    // and place session outline accordingly
    pointerYCoordinate: number | null = null

    // Session outline config variables
    sessionOutlineTop: number | null = null // Default to null when not hovering
    dayColumnWidth: number | null = null
    slotNumber: number | null = null
    pointedAtSessionTime: number | null = null
    pointedAtSessionInfo: CalendarDayAvailablePartner | null = null

    private unavailableRanges: Array<[number, number]>

    // Time line config
    public timeLineTop: number
    public timeLineAnchorTop: number
    public currentTimeInMs: number
    public displayTimeFormat: string

    private dstBoundries: DstBoundaries

    public loadingSkeletonIndices: number[] = []
    public removingLoadingSkeletons: boolean = false
    private currentUser: any = null
    constructor(
        private cdr: ChangeDetectorRef,
        private selectedSessionService: SelectedSessionService,
        private utilsService: UtilsService,
        private sessionSettingsService: SessionSettingsService,
        private availableTimesService: AvailableTimesService,
        private timeUtils: TimeUtilitiesService,
        private dstService: DstBoundaryService,
        private userService: UserService,
        private featureCheckService: FeatureCheckService,
    ) {}

    ngOnInit() {
        this.dstBoundries = this.dstService.getDstBoundaries(this.timezone, this.dayData.startOfDay)
        this.populateTimeSlotData()
        this.updateEmptyTimeslotStatus()

        this.endOfDay = this.timeUtils.getEndOfDay(this.dayData.startOfDay, this.timezone)
        this.updateUnavailableRanges()
        this.filterPotentialPartnerData()

        // In the case where a slot is selected or booked,
        // make sure we remove the outline
        this.updateRectanglePosition(true)

        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user) => {
            this.currentUser = user
            this.updateEmptyTimeslotStatus()
            this.cdr.markForCheck()
        })

        this.availableTimesService.daysBeingLoaded$
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((daysBeingLoaded) => {
                const isLoading = this.loadingSkeletonIndices.length > 0
                const shouldBeLoading = daysBeingLoaded.has(this.dayData.startOfDay)

                if (shouldBeLoading && !isLoading) {
                    this.generateLoadingSkeletons()
                    this.filterLoadingSkeletons()
                    this.cdr.markForCheck()
                } else if (!shouldBeLoading && isLoading && !this.removingLoadingSkeletons) {
                    this.clearLoadingSkeletons()
                    this.cdr.markForCheck()
                }
            })

        // every minute, update the red line and the selectable status of empty slots
        this.timeUtils
            .onMinuteStart()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                if (this.isToday) {
                    this.currentTimeInMs = this.utilsService.getCurrentTimestamp()
                    this.setTimeLineIndicatorPosition()
                    this.updateEmptyTimeslotStatus()
                    this.cdr.markForCheck()
                }
            })
    }

    ngOnChanges(changes: SimpleChanges) {
        let now = this.utilsService.getCurrentTimeForTimezone(this.timezone)
        this.currentTimeInMs = now.valueOf()
        this.dstBoundries = this.dstService.getDstBoundaries(this.timezone, this.dayData.startOfDay)

        if (this.dayContainer) {
            // Ensure wdith is set correctly if number of days changes
            setTimeout(() => {
                this.dayColumnWidth = this.dayContainer.nativeElement.offsetWidth
                this.cdr.detectChanges()
            }, 0)
            this.setTimeLineIndicatorPosition()
            this.updateEmptyTimeslotStatus()
        }
        this.updateUnavailableRanges()
        this.filterPotentialPartnerData()

        // In the case where a slot is selected or booked, make sure we remove the outline
        this.updateRectanglePosition(true)

        // in case we're loading data and received any updates, make sure our loading skellies aren't
        // overlapping anything important (like a confirmed session)
        this.filterLoadingSkeletons()
    }

    private generateLoadingSkeletons() {
        // for every timeslot, give it a 1 in 3 chance of being a loading skeleton
        const loadingSkeletonIndices = []
        for (const timeslot of this.allTimeslots) {
            if (Math.random() <= 0.33) {
                loadingSkeletonIndices.push(timeslot.index)
            }
        }
        this.loadingSkeletonIndices = loadingSkeletonIndices
    }

    private filterLoadingSkeletons() {
        // filter out loading skeletons that are covered by confirmed or selected sessions
        // and also passed the current time
        const filteredLoadingSkeletonIndices = []
        for (const index of this.loadingSkeletonIndices) {
            if (index > MAXIMUM_SELECTABLE_TIMESLOT_NUMBER) {
                continue
            }

            const sessionTime = index * FIFTEEN_MINS_IN_MSECS + this.dayData.startOfDay
            if (
                !this.isSlotCoveredByConfirmedOrSelectedSession(sessionTime) &&
                sessionTime > this.utilsService.getCurrentTimestamp()
            ) {
                filteredLoadingSkeletonIndices.push(index)
            }
        }
        this.loadingSkeletonIndices = filteredLoadingSkeletonIndices
    }

    private clearLoadingSkeletons() {
        this.removingLoadingSkeletons = true
        this.cdr.detectChanges()
        setTimeout(() => {
            this.loadingSkeletonIndices = []
            this.removingLoadingSkeletons = false
            this.cdr.detectChanges()
        }, 400)
    }

    updateUnavailableRanges() {
        this.unavailableRanges = new Array<[number, number]>(0)
        for (const session of this.dayData.selectedSessionData) {
            let addedDstRange = false
            if (this.dstBoundries && this.dstBoundries.endMs) {
                const offsetInMs = this.dstBoundries.offsetDeltaMinutes * 60 * 1000
                const sessionTimeMinusOffset = session.sessionTime - offsetInMs
                const sessionEndTime = session.sessionTime + session.sessionDuration

                // If the session ends during the period of time that is removed from the calendar
                // due to leaving dst (fallback), mark the time unavailable for the period after the
                // offest to align with what we show on the calendar
                if (
                    sessionEndTime >= this.dstBoundries.endMs &&
                    sessionEndTime <= this.dstBoundries.endMs + offsetInMs
                ) {
                    this.unavailableRanges.push([session.sessionTime, sessionEndTime + offsetInMs])
                    addedDstRange = true
                }

                // If the session starts during the period directly after the fallback time
                // then set the unavailable time to include the start time minus the offset
                // to the actual session end time
                if (
                    sessionTimeMinusOffset >= this.dstBoundries.endMs &&
                    sessionTimeMinusOffset <= this.dstBoundries.endMs + offsetInMs
                ) {
                    this.unavailableRanges.push([sessionTimeMinusOffset, sessionEndTime])
                    addedDstRange = true
                }
            }

            if (!addedDstRange) {
                this.unavailableRanges.push([
                    session.sessionTime,
                    session.sessionTime + session.sessionDuration,
                ])
            }
        }
        if (this.dayData.bookedSessionData) {
            for (const session of this.dayData.bookedSessionData) {
                let addedDstRange = false
                const sessionStartTime = session.startMs
                const sessionEndTime = session.startMs + session.durationMs

                if (this.dstBoundries && this.dstBoundries.endMs) {
                    const offsetInMs = this.dstBoundries.offsetDeltaMinutes * 60 * 1000
                    const sessionTimeMinusOffset = sessionStartTime - offsetInMs

                    // If the session ends during the period of time that is removed from the calendar
                    // due to leaving dst (fallback), mark the time unavailable for the period after the
                    // offest to align with what we show on the calendar
                    if (
                        sessionEndTime >= this.dstBoundries.endMs &&
                        sessionEndTime <= this.dstBoundries.endMs + offsetInMs
                    ) {
                        this.unavailableRanges.push([sessionStartTime, sessionEndTime + offsetInMs])
                        addedDstRange = true
                    }

                    // If the session starts during the period directly after the fallback time
                    // then set the unavailable time to include the start time minus the offset
                    // to the actual session end time
                    if (
                        sessionTimeMinusOffset >= this.dstBoundries.endMs &&
                        sessionTimeMinusOffset <= this.dstBoundries.endMs + offsetInMs
                    ) {
                        this.unavailableRanges.push([sessionTimeMinusOffset, sessionEndTime])
                        addedDstRange = true
                    }
                }

                if (!addedDstRange) {
                    this.unavailableRanges.push([sessionStartTime, sessionEndTime])
                }
            }
        }

        if (this.dayData.bookedGroupMeetingData) {
            for (const groupBooking of this.dayData.bookedGroupMeetingData) {
                const startTime = groupBooking.startMs
                const endTime = groupBooking.startMs + groupBooking.durationMs
                this.unavailableRanges.push([startTime, endTime])
            }
        }
    }

    private filterPotentialPartnerData(): void {
        const localPotentialPartnerData = []

        for (const potentialPartner of this.dayData.potentialPartnerData) {
            if (!this.isSlotCoveredByConfirmedOrSelectedSession(potentialPartner.sessionTime)) {
                localPotentialPartnerData.push(potentialPartner)
            }
        }

        this.filteredPotentialPartnerData = [...localPotentialPartnerData]
    }

    onWindowResized(viewport: WindowResizedType) {
        this.dayColumnWidth = viewport.width
    }

    onPointerMove(event: PointerEvent) {
        if (event.pointerType === 'mouse') {
            // Calculate the Y position relative to the top of the scrollable container
            this.pointerYCoordinate =
                event.clientY -
                this.dayContainer.nativeElement.getBoundingClientRect().top +
                this.dayContainer.nativeElement.scrollTop

            this.updateRectanglePosition()
        }
    }

    onPointerLeave() {
        this.sessionOutlineTop = null // Don't show session outline
        this.pointerYCoordinate = null // Reset Y coordinate position
    }

    updateRectanglePosition(removeOnly: boolean = false) {
        if (this.pointerYCoordinate !== null && this.dayContainer) {
            const top =
                Math.floor(this.pointerYCoordinate / TIMESLOT_HEIGHT) * TIMESLOT_HEIGHT -
                this.dayContainer.nativeElement.scrollTop
            // Determine which session timeslot is pointed at
            this.slotNumber = Math.floor(top / TIMESLOT_HEIGHT)
            this.pointedAtSessionTime = this.allTimeslots[this.slotNumber]?.startTime || 0
            this.pointedAtSessionInfo = this.getPotentialPartnerInfo(this.pointedAtSessionTime)

            if (this.timeslotIsSelectable(this.pointedAtSessionTime, this.slotNumber)) {
                if (!removeOnly) {
                    this.sessionOutlineTop = top
                }
            } else {
                this.sessionOutlineTop = null
            }
        }
    }

    timeslotIsSelectable(sessionTime: number, timeSlotNumber: number): boolean {
        const currentTime = this.utilsService.getCurrentTimestamp()
        const gracePeriodCutoff = currentTime - FIVE_MINUTES_IN_MSECS
        const isPastStartTime = sessionTime < currentTime
        const isWithinGracePeriod = sessionTime > gracePeriodCutoff

        // If more than 5 minutes in the past, not selectable regardless of session count
        if (sessionTime <= gracePeriodCutoff) {
            return false
        }

        // If past start time but within grace period, check if user has minimum required sessions (same as Focus Now gating)
        if (isPastStartTime && isWithinGracePeriod) {
            if (!this.currentUser) {
                return false
            }
            // Use the same logic as Focus Now - must have 3+ sessions or be on paid plan
            const canBookLate = this.featureCheckService.canUseNowSessions(this.currentUser)
            if (!canBookLate) {
                return false
            }
        }

        if (timeSlotNumber > MAXIMUM_SELECTABLE_TIMESLOT_NUMBER) {
            return false
        }
        return this.canScheduleMeeting(sessionTime, this.sessionDuration)
    }

    private canScheduleMeeting(
        startTime: number, // Meeting start time in milliseconds
        duration: number, // Meeting duration in milliseconds
    ): boolean {
        const meetingEndTime = startTime + duration
        const unavailableRanges = this.unavailableRanges

        if (this.unavailableRanges) {
            for (const [unavailableStart, unavailableEnd] of unavailableRanges) {
                // Check if the meeting overlaps with the current unavailable range
                if (startTime <= unavailableEnd && meetingEndTime >= unavailableStart) {
                    // The meeting overlaps with an unavailable time slot
                    return false
                }
            }
        }

        // No overlap found, meeting can be scheduled
        return true
    }

    private isSlotCoveredByConfirmedOrSelectedSession(sessionTime: number): boolean {
        const unavailableRanges = this.unavailableRanges

        if (this.unavailableRanges) {
            for (const [unavailableStart, unavailableEnd] of unavailableRanges) {
                // Check if the meeting overlaps with the current unavailable range
                if (sessionTime <= unavailableEnd && sessionTime >= unavailableStart) {
                    // The meeting overlaps with an unavailable time slot
                    return true
                }
            }
        }

        // No overlap found
        return false
    }

    public onClickSelectSlot(event): void {
        const pointerYCoordinate =
            event.clientY -
            this.dayContainer.nativeElement.getBoundingClientRect().top +
            this.dayContainer.nativeElement.scrollTop

        const timeSlot = Math.floor(pointerYCoordinate / TIMESLOT_HEIGHT)
        const sessionTime = this.allTimeslots[timeSlot].startTime

        if (this.timeslotIsSelectable(sessionTime, timeSlot)) {
            const sessionPreferenceFavorites =
                this.sessionSettingsService.getSessionSettingFavoritesPreference()
            const sessionActivityType = this.sessionSettingsService.getSessionSettingActivityType()
            const sessionQuietMode = this.sessionSettingsService.quietModeEnabled()

            const partnerData = this.getPotentialPartnerInfo(sessionTime)

            this.selectedSessionService.addSelectedSession({
                sessionDuration: this.sessionDuration,
                sessionTime: sessionTime,
                userAvailabilityCode: '',
                sessionPreferenceFavorites: sessionPreferenceFavorites,
                activityType: sessionActivityType,
                quietMode: sessionQuietMode,
                user: partnerData?.partner?.displayName ?? '',
                profileUrl: partnerData?.partner?.profileSlug ?? '',
            })
        }
    }

    private getPotentialPartnerInfo(sessionTime: number): CalendarDayAvailablePartner | null {
        for (const session of this.dayData.potentialPartnerData) {
            if (session.sessionTime === sessionTime) {
                return session
            }
        }
        return null
    }

    private setTimeLineIndicatorPosition() {
        if (this.timeFormat == '12hr') {
            this.displayTimeFormat = 'h:mma'
        } else {
            this.displayTimeFormat = 'HH:mm'
        }

        let now = this.utilsService.getCurrentTimeForTimezone(this.timezone)
        let hour = now.hour()
        let top = hour * (TIMESLOT_HEIGHT * 4) + (now.minute() / 60) * (TIMESLOT_HEIGHT * 4)

        this.timeLineTop = top
        this.timeLineAnchorTop = top - 5
    }

    private updateEmptyTimeslotStatus() {
        // for the empty timeslots, this information is currently only used to show a different color
        // for past timeslots.
        const currentTime = this.currentTimeInMs || this.utilsService.getCurrentTimestamp()
        const canBookLate = this.currentUser
            ? this.featureCheckService.canUseNowSessions(this.currentUser)
            : false

        for (const timeslot of this.allTimeslots) {
            const isPastStartTime = timeslot.startTime < currentTime
            const isPastGracePeriod =
                timeslot.startTime < currentTime - LATE_BOOKING_GRACE_PERIOD_MINUTES * 60 * 1000

            // If past start time but within grace period, check if user can book late
            if (isPastStartTime && !isPastGracePeriod) {
                timeslot.isSelectable = canBookLate
            } else if (isPastGracePeriod) {
                timeslot.isSelectable = false
            } else {
                timeslot.isSelectable = true
            }
        }
    }

    private populateTimeSlotData(): void {
        this.allTimeslots = []

        const SLOT_MS = FIFTEEN_MINS_IN_MSECS
        const startOfDay = this.dayData.startOfDay

        // Normalize the DST change into 15-minute slots (can be 0, 2, 4, etc.)
        const offsetDeltaMin = this.dstBoundries?.offsetDeltaMinutes ?? 0
        const deltaSlots = Math.abs(Math.trunc(offsetDeltaMin / 15)) // integer count of slots shifted

        // Determine if a transition happens during this day and its direction
        const startTransitionInDay =
            this.dstBoundries?.startMs != null &&
            this.dstBoundries.startMs >= startOfDay &&
            this.dstBoundries.startMs < this.timeUtils.getEndOfDay(startOfDay, this.timezone)
                ? this.dstBoundries.startMs
                : null

        const endTransitionInDay =
            this.dstBoundries?.endMs != null &&
            this.dstBoundries.endMs >= startOfDay &&
            this.dstBoundries.endMs < this.timeUtils.getEndOfDay(startOfDay, this.timezone)
                ? this.dstBoundries.endMs
                : null

        // Direction of change on this day:
        //   +1 = clocks move forward (DST starts)  → time skips ahead
        //   -1 = clocks move backward (DST ends)   → time repeats (fallback)
        //    0 = no DST change on this day
        const direction = startTransitionInDay ? +1 : endTransitionInDay ? -1 : 0

        // Transition moment for this day (ms since epoch), if any
        const transitionMs = startTransitionInDay ?? endTransitionInDay ?? null

        // Which slot index does the transition align to? (clamped to grid)
        const transitionSlot =
            transitionMs == null
                ? null
                : Math.max(
                      0,
                      Math.min(
                          NUM_TIMESLOTS_PER_DAY - 1,
                          Math.floor((transitionMs - startOfDay) / SLOT_MS),
                      ),
                  )

        // Helper to create a slot entry
        const makeSlot = (
            startTime: number,
            { isDst = false, index }: { isDst?: boolean; index: number },
        ) => ({
            startTime,
            isDst,
            isSelectable: true,
            index,
        })

        if (direction === 0 || deltaSlots === 0 || transitionSlot == null) {
            // No DST change today — straightforward fill
            for (let i = 0; i < NUM_TIMESLOTS_PER_DAY; i++) {
                const t = startOfDay + i * SLOT_MS
                this.allTimeslots.push(makeSlot(t, { index: i }))
            }
            return
        }

        if (direction > 0) {
            // SPRING FORWARD (DST starts): a block of slots is *skipped*.
            // We emit placeholders (startTime = 0) for the skipped window to keep a fixed grid,
            // and mark them as isDst=true (meaning “DST skip here”).
            let skipped = false
            for (let i = 0; i < NUM_TIMESLOTS_PER_DAY; i++) {
                if (i >= transitionSlot && i < transitionSlot + deltaSlots) {
                    // skipped slots window
                    this.allTimeslots.push(makeSlot(0, { isDst: true, index: i }))
                    skipped = true
                } else {
                    const t = skipped
                        ? startOfDay + (i - deltaSlots) * SLOT_MS
                        : startOfDay + i * SLOT_MS
                    this.allTimeslots.push(makeSlot(t, { index: i }))
                }
            }
            return
        }

        // FALL BACK (DST ends): we skip the *second* 1AM entirely,
        // and mark the first 2AM Standard Time slot as isDst=true.
        //
        // Implementation detail:
        // - For indices < transitionSlot: normal
        // - For indices >= transitionSlot: add +offsetMs to jump past the repeated hour
        //   so the sequence goes ... 1:45 → 2:00 (no duplicate 1AM).
        // - The first index at/after the transition (i === transitionSlot) becomes 2:00 ST;
        //   mark it with { isDst: true }.

        const offsetMs = deltaSlots * SLOT_MS

        for (let i = 0; i < NUM_TIMESLOTS_PER_DAY; i++) {
            const base = startOfDay + i * SLOT_MS

            if (i < transitionSlot) {
                // Before the switch — regular mapping
                this.allTimeslots.push({
                    startTime: base,
                    isDst: false,
                    isSelectable: true,
                    index: i,
                })
                continue
            }

            // At / after the switch — jump forward by +offsetMs, thereby skipping the second 1AM.
            const mapped = base + offsetMs

            // The very first mapped slot is the first "2:00 AM Standard Time".
            this.allTimeslots.push({
                startTime: mapped,
                isDst: false,
                isSelectable: true,
                index: i,
            })
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
